import { authFetch, clearToken } from "../utils/auth";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Target, ChevronRight, Zap, TrendingUp, Users } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = window.location.origin;

const STEPS = [
  {
    title: "Start a Mission",
    desc: "Pick your time and location. We'll build a structured, adaptive session just for you — whether you have 15 minutes or 2 hours.",
    icon: <Zap className="w-10 h-10" />,
    color: "text-emerald-700",
    bg: "bg-emerald-500/15",
    ring: "ring-emerald-500/30"
  },
  {
    title: "Track Your Progress",
    desc: "After each drill, give quick feedback. We'll track your streaks, practice hours, and skill growth over time.",
    icon: <TrendingUp className="w-10 h-10" />,
    color: "text-blue-400",
    bg: "bg-blue-500/15",
    ring: "ring-blue-500/30"
  },
  {
    title: "Connect with Your Coach",
    desc: "Got a coach? They can assign drills that blend into your sessions. Accept an invite link and you're set.",
    icon: <Users className="w-10 h-10" />,
    color: "text-purple-400",
    bg: "bg-purple-500/15",
    ring: "ring-purple-500/30"
  }
];

export default function WelcomeTour() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);

  const complete = async () => {
    try {
      await authFetch(`${API}/api/profile/welcome-tour`, {
        method: "POST",
      });
    } catch (e) { console.warn("Welcome tour completion failed:", e); }
    navigate("/dashboard");
  };

  const next = () => {
    if (step < STEPS.length - 1) {
      setDirection(1);
      setStep(step + 1);
    } else {
      complete();
    }
  };

  const prev = () => {
    if (step > 0) {
      setDirection(-1);
      setStep(step - 1);
    }
  };

  const current = STEPS[step];

  const variants = {
    enter: (d) => ({ x: d > 0 ? 200 : -200, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -200 : 200, opacity: 0 })
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="flex items-center gap-2 mb-10">
        <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center">
          <Target className="w-5 h-5 text-[#111]" />
        </div>
        <span className="font-bold text-lg text-[#111]">GolfFlowPro</span>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm relative overflow-hidden" style={{ minHeight: 320 }}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="text-center"
          >
            <div className={`w-20 h-20 rounded-2xl ${current.bg} ring-1 ${current.ring} flex items-center justify-center mx-auto mb-6 ${current.color}`}>
              {current.icon}
            </div>
            <h2 className="text-2xl font-bold text-[#111] mb-3" data-testid={`tour-title-${step}`}>
              {current.title}
            </h2>
            <p className="text-[#555] leading-relaxed px-2">
              {current.desc}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dots */}
      <div className="flex gap-2 mb-8">
        {STEPS.map((_, i) => (
          <button
            key={i}
            onClick={() => { setDirection(i > step ? 1 : -1); setStep(i); }}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === step ? "w-6 bg-emerald-400" : "w-2 bg-white/20"
            }`}
            data-testid={`tour-dot-${i}`}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="w-full max-w-sm flex gap-3">
        {step > 0 && (
          <Button
            variant="ghost"
            onClick={prev}
            className="text-[#555] hover:text-[#111] px-6"
            data-testid="tour-prev-btn"
          >
            Back
          </Button>
        )}
        <Button
          onClick={next}
          className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-5 text-base"
          data-testid="tour-next-btn"
        >
          {step === STEPS.length - 1 ? "Get Started" : "Next"}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Skip */}
      {step < STEPS.length - 1 && (
        <button
          onClick={complete}
          className="mt-4 text-[#555] text-sm hover:text-[#555] transition-colors"
          data-testid="tour-skip-btn"
        >
          Skip tour
        </button>
      )}
    </div>
  );
}
